/**
 * Сборка занятия из пользовательского текста.
 *
 * Упражнение собирается тем же генератором и по той же смеси материала, что и
 * обычная тренировка: правила разведения повторов и инварианты не должны
 * зависеть от того, откуда пришли слова (ТЗ FR-EN-19, §16.2).
 *
 * Текст остаётся на устройстве (ТЗ SEC-07): сюда он приходит из формы, здесь
 * же разбирается на слова, и ни одна строка отсюда не уходит в сеть. На сервер
 * от занятия уходит только агрегат без единиц — это проверяет
 * `apps/web/tests/custom-text.test.tsx`.
 *
 * Контрольного материала у режима нет: он по определению не пересекается с
 * тренировочным, а другого текста, кроме принесённого, здесь нет. Поэтому его
 * доля — ноль, а не те же слова под другим именем: подменить контрольный
 * набор тренировочным значит сломать Transfer Guard (ТЗ §7.6).
 */

import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';
import { WEAK_THRESHOLD, toMasteryUnitId } from '@type2x/shared-contracts';
import { getLayout } from '@type2x/keyboard-layouts';
import {
  DEFAULT_LEARNING_CONFIG,
  isDue,
  normalizeMix,
  type LearningConfig,
} from '@type2x/learning-engine';
import { generateExercise, type CandidatePools } from '@type2x/exercise-generator';

import type { CustomTextPlan, CustomTextSnapshot, ParsedText } from '../contracts/custom-text.js';
import {
  CUSTOM_TEXT_HINT_LEVEL,
  CUSTOM_TEXT_ITEMS,
  CUSTOM_UNIT_PREFIX,
} from '../contracts/custom-text.js';

/** Учебная единица слова пользователя. */
export function customUnit(
  snapshot: CustomTextSnapshot,
  word: string,
  frequency: number,
): MasteryUnit {
  return {
    id: toMasteryUnitId(`${CUSTOM_UNIT_PREFIX}${word}`),
    kind: 'word',
    content: word,
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    // Частота — доля слова в принесённом тексте, а не в языке: пользователь
    // тренирует то, что печатает он, а не то, что печатают все.
    realWorldFrequency: frequency,
  };
}

/** Куда отнести единицу: слабое, пора повторить, поддержание или новое. */
function bucketOf(
  stat: MasteryStat | undefined,
  nowMs: number,
): 'weak' | 'due' | 'maintenance' | 'fresh' {
  if (stat === undefined || stat.attempts === 0) return 'fresh';
  if (stat.masteryScore < WEAK_THRESHOLD) return 'weak';
  return isDue(stat, nowMs) ? 'due' : 'maintenance';
}

/** Раскладывает слова текста по пулам генератора. */
function buildPools(
  snapshot: CustomTextSnapshot,
  units: readonly MasteryUnit[],
  nowMs: number,
): CandidatePools {
  const weak: MasteryUnit[] = [];
  const due: MasteryUnit[] = [];
  const maintenance: MasteryUnit[] = [];
  const fresh: MasteryUnit[] = [];

  for (const unit of units) {
    const bucket = bucketOf(snapshot.stats[unit.id], nowMs);
    ({ weak, due, maintenance, fresh })[bucket].push(unit);
  }

  // Пустой пул сделал бы упражнение короче заказанного: недостающий материал
  // берётся из того, что в тексте есть.
  const fallback = fresh.length > 0 ? fresh : [...units];

  return {
    weak: weak.length > 0 ? weak : fallback,
    due: due.length > 0 ? due : fallback,
    fresh: fresh.length > 0 ? fresh : fallback,
    maintenance: maintenance.length > 0 ? maintenance : fallback,
    transfer: [],
  };
}

/**
 * Строит занятие по разобранному тексту.
 *
 * @param snapshot снимок навыка;
 * @param parsed разбор пользовательского текста;
 * @param seed зерно генератора: то же занятие воспроизводимо;
 * @param nowMs текущее время в миллисекундах: по нему считается срок повторения;
 * @param config конфигурация обучающего алгоритма.
 */
export function buildCustomTextPlan(
  snapshot: CustomTextSnapshot,
  parsed: ParsedText,
  seed: string,
  nowMs: number,
  config: LearningConfig = DEFAULT_LEARNING_CONFIG,
): CustomTextPlan {
  const units = parsed.words.map((word) => customUnit(snapshot, word.text, word.frequency));
  const layout = getLayout(snapshot.layoutId, snapshot.platform);
  const allowedCharacters = new Set(layout.keys.flatMap((key) => [key.base, key.shifted]));

  const exercise = generateExercise({
    seed,
    mode: 'custom-text',
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    itemCount: CUSTOM_TEXT_ITEMS,
    // Доля контрольного материала обнуляется и расходится по остальным
    // пропорционально: в принесённом тексте контрольного материала нет.
    mix: normalizeMix({ ...config.baseMix, transfer: 0 }),
    pools: buildPools(snapshot, units, nowMs),
    hintLevel: CUSTOM_TEXT_HINT_LEVEL,
    allowedCharacters,
    maxConsecutiveRepeats: config.maxConsecutiveRepeats,
  });

  return {
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    exercise,
    units,
    hintLevel: CUSTOM_TEXT_HINT_LEVEL,
    rejectedWords: parsed.rejectedWords,
    unsupportedCharacters: parsed.unsupportedCharacters,
  };
}
